import {formatBlobRange, parseBlobRange} from '@github-ui/blob-anchor'
import type {
  SourceRange as TaillightsSourceRange,
  SourceSelection as TaillightsSourceSelection,
} from '@github-ui/taillights-worker/document'

export type SourceRange = TaillightsSourceRange & {
  /** Whole-line intent includes the end row even when its text is empty. */
  endLineInclusive?: true
  /** Column intent from a linked range that names its columns, even when they span whole lines. */
  columns?: true
}

export function lineRange(lines: readonly string[], start: number, end = start): SourceRange {
  return {start: {line: start, column: 0}, end: {line: end, column: lines[end]?.length ?? 0}, endLineInclusive: true}
}

/** Whether a range covers whole lines, as a line anchor does, rather than columns within them. */
export function isLineRange(range: SourceRange, lines: readonly string[]) {
  if (range.columns || range.start.column !== 0) return false
  // A native selection ending at the next row's column zero covers its last row whole.
  if (lastSelectedLine(range) < range.end.line) return true
  return range.end.column === (lines[range.end.line]?.length ?? 0)
}

export function rangeFromAnchor(anchor: string, lines: readonly string[]): SourceRange | null {
  if (!/^#?L\d+(?:C\d+)?(?:-L\d+(?:C\d+)?)?$/.test(anchor)) return null
  const range = parseBlobRange(anchor)
  if (!range || range.start.line < 1 || range.end.line > lines.length) return null
  for (const point of [range.start, range.end]) {
    if (point.column !== null && (point.column < 1 || point.column > (lines[point.line - 1]?.length ?? 0) + 1)) {
      return null
    }
  }
  return {
    start: {line: range.start.line - 1, column: range.start.column === null ? 0 : range.start.column - 1},
    end: {
      line: range.end.line - 1,
      column: range.end.column === null ? (lines[range.end.line - 1]?.length ?? 0) : range.end.column - 1,
    },
    ...(range.end.column === null ? {endLineInclusive: true} : {}),
    ...(range.start.column !== null || range.end.column !== null ? {columns: true} : {}),
  }
}

export function anchorFromRange(range: TaillightsSourceRange): string {
  return `#${formatBlobRange({
    start: {line: range.start.line + 1, column: null},
    end: {line: range.end.line + 1, column: null},
  })}`
}

export function anchorFromSelection(range: SourceRange, lines?: readonly string[]): string {
  if (
    lines &&
    (range.start.column !== 0 || (range.end.column !== lines[range.end.line]?.length && range.end.column !== 0))
  ) {
    return `#${formatBlobRange({
      start: {line: range.start.line + 1, column: range.start.column + 1},
      end: {
        line: range.end.line + 1,
        column: range.endLineInclusive && range.end.column === 0 ? null : range.end.column + 1,
      },
    })}`
  }
  return anchorFromRange({...range, end: {...range.end, line: lastSelectedLine(range)}})
}

export function lastSelectedLine(range: SourceRange): number {
  // A native selection ending at the next row's column zero does not include that row.
  return !range.endLineInclusive && range.end.line > range.start.line && range.end.column === 0
    ? range.end.line - 1
    : range.end.line
}

export function gutterSelection(
  lines: readonly string[],
  line: number,
  extend: boolean,
  native: TaillightsSourceSelection | null,
  anchor: SourceRange | null,
): {range: SourceRange; preserve: TaillightsSourceSelection | null} {
  if (
    native &&
    (native.start.line !== native.end.line || native.start.column !== native.end.column) &&
    line >= native.start.line &&
    line <= lastSelectedLine(native)
  ) {
    return {range: native, preserve: native}
  }
  const start = extend && anchor ? anchor.start.line : line
  const range = lineRange(lines, Math.min(start, line), Math.max(start, line))
  if (extend && anchor && anchor.start.column > 0) {
    if (start <= line) range.start = anchor.start
    else range.end = anchor.start
  }
  return {range, preserve: null}
}

export function selectedText(lines: readonly string[], range: TaillightsSourceRange): string {
  if (range.start.line === range.end.line) {
    return lines[range.start.line]?.slice(range.start.column, range.end.column) ?? ''
  }
  return [
    lines[range.start.line]?.slice(range.start.column) ?? '',
    ...lines.slice(range.start.line + 1, range.end.line),
    lines[range.end.line]?.slice(0, range.end.column) ?? '',
  ].join('\n')
}

export function displayedRange(lineNumbers: readonly number[], range: SourceRange) {
  function lowerBound(line: number) {
    let left = 0
    let right = lineNumbers.length
    while (left < right) {
      const middle = (left + right) >>> 1
      if ((lineNumbers[middle] ?? 0) < line) left = middle + 1
      else right = middle
    }
    return left
  }
  const start = lowerBound(range.start.line)
  const end = lowerBound(lastSelectedLine(range) + 1)
  return start < end ? {start, end} : null
}
